import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { ApiUrlConstant } from "@constants/apiUrls";
import type { EvaluationTaskV3 } from "@services/evaluationService";

export interface ReportIssueRequest {
  uamGateWayId: number;
  examId: string;
  pageNumbers: string;
  reasons: string[];
  taskId: string;
}

export interface ReportIssueResponse {
  message?: string;
}

export interface ReportIssueContext {
  uamGateWayId: number;
  examId: string;
  taskId: string;
}

function getAccessToken(): string {
  const token = localStorage.getItem("accessToken");
  if (!token) {
    throw new Error("Access token not found in localStorage");
  }
  return token;
}

function createMetaApiInstance() {
  const accessToken = getAccessToken();

  return axios.create({
    baseURL: ApiUrlConstant.metaUrl,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

function toLongId(value: number | string | undefined | null): number | null {
  if (value === undefined || value === null || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getReportIssueContextFromTaskV3(
  task: EvaluationTaskV3
): ReportIssueContext | null {
  const uamGateWayId = toLongId(
    task.exam_submission?.uam_id ??
      task.exam_submission?.submission_by ??
      task.submission_by
  );

  const examId = task.exam_submission?.exam_id ?? "";
  const taskId = task.assignment_id ?? task.id ?? "";

  if (uamGateWayId === null || !examId || !taskId) {
    return null;
  }

  return {
    uamGateWayId,
    examId,
    taskId,
  };
}

export async function reportIssue(
  payload: ReportIssueRequest
): Promise<ReportIssueResponse> {
  try {
    const api = createMetaApiInstance();
    const response = await api.post<ReportIssueResponse>(
      "v1/subjective/reportIssue",
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Error reporting issue:", error);
    throw (
      error?.response?.data ||
      error?.message ||
      new Error("Failed to report issue")
    );
  }
}

export function useReportIssue() {
  return useMutation({
    mutationFn: (payload: ReportIssueRequest) => reportIssue(payload),
  });
}
